import { Controller } from "@hotwired/stimulus"
import { WEB_EVENTS } from "../lib/web_events"
import { ensureMapLibre } from "./vendor_loader"
import { createBaseMap, applyBasemap, loadBaseStyle } from "../lib/maplibre_map"
import { bboxParams } from "../lib/bbox"
import { fetchJson } from "../lib/api"
import { logEvent } from "../lib/client_logger"
import { CameraController } from "../lib/map_camera_controller"
import { MAP_POPUP_MAX_WIDTH, featurePopupPlan, mapOverlayOwnsClick, pinMarkerPlan, trailStartMarkerPlan } from "../lib/map_popups"
import { cardinal } from "../lib/friends"
import { presenceHeartbeatBody, presencePollIntervalSec } from "../lib/presence"
import { PresenceCoordinator } from "../lib/presence_coordinator"
import {
  activeLayersNeedData,
  centerMovedSinceLastSearch,
  shouldAutoSearchAfterMove
} from "../lib/map_auto_search_policy"
import { metersBetweenLatLon } from "../lib/geo_math"
import {
  layerSearchStatus,
  mapLayerSearchPlan
} from "../lib/map_layer_search_policy"
import {
  basemapChoiceState,
  mapSheetTabState,
} from "../lib/map_control_state"
import {
  GEOJSON_SOURCE_IDS,
  INTERACTIVE_LAYERS,
  MANAGED_TRAIL_LAYERS,
  MANAGED_TRAIL_SOURCES,
  clearLayerPlan,
  layerLoadPlan,
  overlayLayerDefinitions,
  polygonLabelPoint,
  vectorLayerKeyForStyleLayer
} from "../lib/map_layer_view_policy"
import { selectedTrailUseKeys, trailUseAllowed } from "../lib/map_trail_use_policy"
import {
  WEB_DEFAULT_SETTINGS, buildStoredMapPrefs, coerceWebSetting, mapPrefsLoadState,
  readStoredMapPrefs, webSettingControlState, writeStoredMapPrefs
} from "../lib/web_settings"
import { initialMapPreferences, saveMapPreferences } from "../lib/map_preferences"
import {
  metersToFeet,
  metersToMiles,
  shouldRefreshElevation,
  speedMphLabel
} from "../lib/web_location_policy"
import { MAP_VIEW_POLICY, leafletZoomToMapLibre } from "../lib/map_view_policy"
import { webTrackingTransition } from "../lib/web_tracking_state"
import { responseIsCurrent } from "../lib/request_freshness"
import {
  safeStorageGet, trackerPausedForTrip, trackerStoredTrackingSeed,
  TRACKER_DISCARD_KEY, TRACKER_FINISH_KEY, TRACKER_PAUSE_KEY, TRACKER_STOP_KEY, TRACKER_TRIP_KEY
} from "../lib/tracker_storage_policy"
import {
  WEB_TRACK_LINES_POLICY, appendTrackPoint, friendTrackFeatures,
  selfTrackFeature, trackLineLayerDefinitions
} from "../lib/track_lines_policy"
import { WEB_MAP_POLICY } from "../lib/web_policy"

// MapLibre GL is a shared global; load it from the controller so Turbo visits
// can initialize the map without needing a full page refresh.
let maplibregl
const LAYER_SEARCH_TIMEOUT_MS = WEB_MAP_POLICY.layerSearchTimeoutMs

// Drives the MapLibre map: layer toggles, "search this area", rendering markers
// from the Rails proxy endpoints, the follow/orientation/3D camera (ported from
// the iOS app), and the USGS gauge readings modal.
export default class extends Controller {
  static targets = [
    "container", "status", "layerToggle", "autoSearch", "locateButton", "useFilter",
    "hud", "hudSpeed", "hudDistance", "hudElevation", "hudDir", "hudArrow",
    "liveButton", "followButton", "orientButton", "pitchButton", "layersModal", "moreModal", "settingsModal",
    "basemapTab", "layersTab", "basemapPanel", "layersPanel", "basemapChoice", "friendsPill", "invitesPill"
  ]
  static values = {
    layerUrls: Object, usgsSiteUrl: String, elevationUrl: String, presenceUrl: String,
    tripInvitesUrl: String, preferencesUrl: String, preferences: Object, layerLimits: Object,
    signedIn: Boolean, initialLat: Number, initialLng: Number, initialZoom: Number
  }

  connect() {
    this.connected = true
    this.searchRequestId = 0
    this._elevRequestId = 0
    this.layers = {}
    this.managedTrailFeatures = {}
    this._featureData = {}
    this.layerFeatureCounts = {}
    this.#boot().catch(error => this.#bootFailed(error))
  }

  async #boot() {
    await ensureMapLibre()
    if (!this.connected) return
    maplibregl = window.maplibregl

    // Initial center from the visitor's IP (server-provided); browser geolocation refines it.
    const lat = this.hasInitialLatValue ? this.initialLatValue : MAP_VIEW_POLICY.defaultHome.lat
    const lng = this.hasInitialLngValue ? this.initialLngValue : MAP_VIEW_POLICY.defaultHome.lng
    const zoom = leafletZoomToMapLibre(this.hasInitialZoomValue ? this.initialZoomValue : MAP_VIEW_POLICY.mapDefaultZoom)
    const style = await loadBaseStyle()
    if (!this.connected) return
    this.map = createBaseMap(maplibregl, this.containerTarget, { lat, lng, zoom, style })
    await new Promise(resolve => this.map.on("load", resolve))
    if (!this.connected) return

    // One overlay per source: vector overlays get a GeoJSON source + style
    // layers, marker overlays collect DOM markers. `data` keeps the original
    // features so popups see real nested properties (MapLibre stringifies
    // nested values on rendered features).
    const layerUrls = this.hasLayerUrlsValue ? this.layerUrlsValue : {}
    this.layers = Object.fromEntries(
      Object.entries(layerUrls).map(([key, url]) => [key, { url, markers: [] }])
    )
    this.managedTrailFeatures = {}
    this._featureData = {}
    this.layerFeatureCounts = {}
    this.#addOverlayLayers()

    // Restore the user's saved control choices.
    this.#loadPrefs()
    this.layerToggleTargets.forEach(t => {
      t.addEventListener("change", () => this.#onToggle(t))
    })
    this.#applyBasemap(this.basemapMode || "streets")

    this.searching = false
    this.searchQueued = false
    this.#resetMoveIntent()
    this.cameraController = new CameraController({
      map: this.map,
      controls: {
        live: this.hasLiveButtonTarget ? this.liveButtonTarget : null,
        follow: this.hasFollowButtonTarget ? this.followButtonTarget : null,
        orient: this.hasOrientButtonTarget ? this.orientButtonTarget : null,
        pitch: this.hasPitchButtonTarget ? this.pitchButtonTarget : null
      },
      getSetting: key => this.#setting(key),
      onSettingsChange: (patch, announcement) => this.#updateCameraSettings(patch, announcement),
      onLocation: ({ lat, lon }) => {
        this.#drawPuck([lat, lon])
        this.#updateElevation(lat, lon)
      },
      onPanMove: () => { this._panMove = true },
      onZoomMove: () => { this._zoomMove = true },
      onMoveEnd: () => { if (!this.#autoOn()) this.#resetMoveIntent() },
      onForceAutoSearch: () => { this._forceNextAutoSearch = true },
      autoSearchEnabled: () => this.#autoOn(),
      search: () => this.search(),
      setStatus: status => this.setStatus(status),
      showLocate: show => this.#showLocate(show),
      isConnected: () => this.connected
    })
    this.cameraController.start()

    // The container is sized by a flex parent; recompute once layout settles,
    // then enter Live through the normal engage path (→ locate → follow).
    requestAnimationFrame(() => {
      if (!this.connected || !this.map) return
      this.map.resize()
      this.#setAuto(this.#autoOn())   // honor the checkbox's default-checked state
      this.cameraController.dispatch({ type: "RECENTER_SELF" })
    })

    // Close the modals on Escape.
    this.escHandler = e => { if (e.key === "Escape") { this.closeLayers(); this.closeMore(); this.closeSettings() } }
    document.addEventListener("keydown", this.escHandler)

    // Follow view, driven by the tracker controller's position feed.
    this.posHandler = e => this.#onTrackPosition(e.detail)
    this.trackHandler = e => this.#setTracking(e.detail.tracking, e.detail.tripId)
    window.addEventListener(WEB_EVENTS.position, this.posHandler)
    window.addEventListener(WEB_EVENTS.tracking, this.trackHandler)

    // Seed tracking from the tracker's persisted state BEFORE the presence
    // poller's first beat (kicked on creation below): an off-trip first beat
    // would write an off-trip presence and run AbandonedTripFinisher against
    // the user's live trip. The query-tracking round-trip at the end of boot
    // still refines this (a re-announce for the same trip is a no-op).
    const seed = trackerStoredTrackingSeed({
      tripId: safeStorageGet(TRACKER_TRIP_KEY),
      stopPending: safeStorageGet(TRACKER_STOP_KEY),
      finishPending: safeStorageGet(TRACKER_FINISH_KEY),
      discardPending: safeStorageGet(TRACKER_DISCARD_KEY)
    })
    this.cameraController.seedTracking(seed.tracking)
    this._tripId = seed.tripId
    this._selfTrack = []

    // Live friends, like the app: poll presence (my heartbeat + their positions)
    // and render markers + the online pill. Signed-out visitors skip it entirely.
    if (this.hasSignedInValue && this.signedInValue && this.hasPresenceUrlValue) {
      this.presenceCoordinator = new PresenceCoordinator({
        map: this.map,
        maplibregl,
        url: this.presenceUrlValue,
        tripInvitesUrl: this.hasTripInvitesUrlValue ? this.tripInvitesUrlValue : null,
        friendsPill: this.hasFriendsPillTarget ? this.friendsPillTarget : null,
        invitesPill: this.hasInvitesPillTarget ? this.invitesPillTarget : null,
        heartbeatBody: () => presenceHeartbeatBody({
          tracking: this.cameraController.tracking,
          tripId: this._tripId,
          last: this.cameraController.last,
          speed: this._speed,
          heading: this.cameraController.course,
          // Paused trips keep the claim but stop sharing the frozen position.
          paused: trackerPausedForTrip(safeStorageGet(TRACKER_PAUSE_KEY), this._tripId)
        }),
        intervalSec: () => presencePollIntervalSec({
          tracking: this.cameraController.tracking,
          tripId: this._tripId,
          locationIntervalSec: this.#setting("locationIntervalSec")
        }),
        getLastLocation: () => this.cameraController.last,
        autoTrackFriends: () => !!this.#setting("autoTrackFriends"),
        onFriendTracks: tracks => this.#renderFriendTracks(tracks),
        onTripStateTerminal: () => this.#onTripStateTerminal(),
        setStatus: status => this.setStatus(status)
      })
      this.presenceCoordinator.start()
    }
    // A mid-trip page load dispatches resume-tracking before this boot finishes;
    // now that the listeners are up, ask the tracker to re-announce.
    window.dispatchEvent(new CustomEvent(WEB_EVENTS.queryTracking))

    // Search bar → recenter. flyTo's moveend triggers the auto layer refresh, so
    // the new area's water/trails load on arrival.
    this.flyHandler = e => {
      if (!this.map) return
      this.cameraController.goToExternal({
        lat: e.detail.lat,
        lon: e.detail.lon,
        zoom: leafletZoomToMapLibre(e.detail.zoom || 12)
      })
    }
    window.addEventListener(WEB_EVENTS.flyTo, this.flyHandler)

  }

  #bootFailed(error) {
    if (!this.connected) return
    logEvent("map.initialize_failed", error)
    if (this.hasStatusTarget) this.statusTarget.textContent = "Map failed to load. Check your connection and try again."
  }

  #setting(key) {
    return this.webSettings?.[key] ?? WEB_DEFAULT_SETTINGS[key]
  }

  locate() {
    this.cameraController?.locate()
  }

  live() {
    this.cameraController?.live()
  }

  async cycleOrient() {
    await this.cameraController?.cycleOrient()
  }

  togglePitch() {
    this.cameraController?.togglePitch()
  }

  toggleFollow() {
    this.cameraController?.toggleFollow()
  }

  #updateCameraSettings(patch, announcement) {
    this.webSettings = { ...WEB_DEFAULT_SETTINGS, ...(this.webSettings || {}), ...patch }
    this.#savePrefs()
    this.#renderSettings()
    if (announcement) window.dispatchEvent(new CustomEvent(WEB_EVENTS.settings, { detail: announcement }))
  }

  focusSearch() {
    const input = document.querySelector('[data-search-target="input"]')
    if (input) {
      input.focus()
      input.select()
      return
    }
    this.search()
  }

  offline() {
    this.setStatus("Offline maps are available in the iOS app.")
  }

  #showLocate(show) {
    if (this.hasLocateButtonTarget) this.locateButtonTarget.classList.toggle("hidden", !show)
  }

  disconnect() {
    this.connected = false
    this.searchRequestId += 1
    this._elevRequestId += 1
    this.searchAbortController?.abort()
    this.searchAbortController = null
    this.cameraController?.stop()
    this.cameraController = null
    this.presenceCoordinator?.stop()
    this.presenceCoordinator = null
    if (this.escHandler) document.removeEventListener("keydown", this.escHandler)
    if (this.posHandler) window.removeEventListener(WEB_EVENTS.position, this.posHandler)
    if (this.trackHandler) window.removeEventListener(WEB_EVENTS.tracking, this.trackHandler)
    if (this.flyHandler) window.removeEventListener(WEB_EVENTS.flyTo, this.flyHandler)
    if (this.map) this.map.remove()
    this.map = null
  }

  // --- layers modal ---------------------------------------------------------

  openLayers() {
    if (!this.hasLayersModalTarget) return
    this.layersModalTarget.classList.remove("hidden")
    this.layersModalTarget.classList.add("flex")
    this.showBasemaps()
  }

  closeLayers() {
    if (!this.hasLayersModalTarget) return
    this.layersModalTarget.classList.add("hidden")
    this.layersModalTarget.classList.remove("flex")
  }

  openMore() {
    if (!this.hasMoreModalTarget) return
    this.moreModalTarget.classList.remove("hidden")
    this.moreModalTarget.classList.add("flex")
  }

  openSettings() {
    this.closeMore()
    if (!this.hasSettingsModalTarget) return
    this.#renderSettings()
    this.settingsModalTarget.classList.remove("hidden")
    this.settingsModalTarget.classList.add("flex")
  }

  closeSettings() {
    if (!this.hasSettingsModalTarget) return
    this.settingsModalTarget.classList.add("hidden")
    this.settingsModalTarget.classList.remove("flex")
  }

  closeMore() {
    if (!this.hasMoreModalTarget) return
    this.moreModalTarget.classList.add("hidden")
    this.moreModalTarget.classList.remove("flex")
  }

  changeSetting(e) {
    const key = e.currentTarget.dataset.settingKey
    if (!key) return
    const value = coerceWebSetting(key, e.currentTarget.value, e.currentTarget.checked)
    this.webSettings = { ...WEB_DEFAULT_SETTINGS, ...(this.webSettings || {}), [key]: value }
    if (key === "followPitch" && Number(value) > 0) this.webSettings.followPitch3d = value
    this.#savePrefs()
    this.#renderSettings()
    this.cameraController?.settingChanged(key)
    if (key === "streetNames") this.#applyBasemap(this.basemapMode || "streets")
    // Toggling a trail setting off erases its line; toggling on starts fresh.
    if (key === "defaultSelfTrack" && !value) {
      this._selfTrack = []
      this.#renderSelfTrack()
    }
    if (key === "autoTrackFriends") this.presenceCoordinator?.settingChanged(key)
    window.dispatchEvent(new CustomEvent(WEB_EVENTS.settings, { detail: { key, value } }))
  }

  showBasemaps() {
    this.#showMapSheetTab("basemaps")
  }

  showLayers() {
    this.#showMapSheetTab("layers")
  }

  selectBasemap(e) {
    this.#applyBasemap(e.currentTarget.dataset.basemap || "streets")
    this.#savePrefs()
  }

  async acceptInvite(e) {
    await this.presenceCoordinator?.acceptInvite(e)
  }

  async declineInvite(e) {
    await this.presenceCoordinator?.declineInvite(e)
  }

  // A presence heartbeat was rejected with a terminal trip-state code: the trip
  // we claimed ended elsewhere. Stop claiming it here and notify the tracker
  // (it owns the recording teardown + Save/Discard handoff).
  #onTripStateTerminal() {
    window.dispatchEvent(new CustomEvent(WEB_EVENTS.tripEnded))
    if (this._tripId) this.#setTracking(false)
  }

  // Recording on/off (driven by the tracker). Owns the HUD + puck; defaults to
  // following when a trip starts. A re-announce for the SAME trip (e.g. after a
  // mid-trip Turbo re-navigation) is a no-op: no odometer reset, no camera jump.
  #setTracking(on, tripId = null) {
    const plan = webTrackingTransition({ on, tripId, currentTripId: this._tripId, hasPuck: !!this.puck })
    // Publish the new tracking state before the immediate presence heartbeat.
    // Camera/UI effects remain ordered after the surrounding HUD cleanup.
    this.cameraController?.prepareTrackingTransition(plan)
    this._tripId = plan.tripId
    if (plan.presenceKick) this.presenceCoordinator?.kick() // share/stop sharing promptly + retune the poll cadence
    if (this.hasHudTarget) {
      this.hudTarget.classList.toggle("hidden", !plan.hudVisible)
      this.hudTarget.classList.toggle("flex", plan.hudVisible)
    }
    if (this.hasFollowButtonTarget) this.followButtonTarget.classList.toggle("hidden", !plan.followButtonVisible)
    if (plan.resetOdometer) {
      this._dist = 0
      this._distLast = null
    }
    // My breadcrumb trail belongs to one trip: a trip end or a NEW trip starts
    // a fresh line; a same-trip re-announce keeps it.
    if (!plan.tracking || plan.resetOdometer) {
      this._selfTrack = []
      this.#renderSelfTrack()
    }
    if (plan.hudDistanceText != null && this.hasHudDistanceTarget) this.hudDistanceTarget.textContent = plan.hudDistanceText
    if (plan.removePuck) { this.puck.remove(); this.puck = null }
    this.cameraController?.completeTrackingTransition(plan)
  }

  #drawPuck(latlng) {
    if (this.puck) {
      this.puck.setLngLat(this.#lngLat(latlng))
    } else {
      const el = document.createElement("div")
      el.className = "map-puck"
      this.puck = new maplibregl.Marker({ element: el }).setLngLat(this.#lngLat(latlng)).addTo(this.map)
    }
  }

  #onTrackPosition({ lat, lon, speed, heading }) {
    const here = [lat, lon]
    // Live odometer: accumulate distance between fixes (miles).
    if (this.cameraController?.tracking) {
      if (this._distLast) this._dist = (this._dist || 0) + metersBetweenLatLon(this._distLast, here)
      this._distLast = here
      if (this.hasHudDistanceTarget) this.hudDistanceTarget.textContent = metersToMiles(this._dist).toFixed(1)
      // "Show my track when recording": grow my breadcrumb line fix by fix.
      if (this.#setting("defaultSelfTrack")) {
        this._selfTrack = appendTrackPoint(this._selfTrack, here, WEB_TRACK_LINES_POLICY.selfTrackMaxPoints)
        this.#renderSelfTrack()
      }
    }
    this._speed = speed
    this.cameraController?.updateTrackingPosition({ lat, lon, heading })
    if (this.hasHudSpeedTarget) {
      this.hudSpeedTarget.textContent = speedMphLabel(speed)
    }
    // Direction widget: rotate the arrow to the GPS heading + show the cardinal.
    if (heading != null && heading >= 0) {
      if (this.hasHudArrowTarget) this.hudArrowTarget.style.transform = `rotate(${heading}deg)`
      if (this.hasHudDirTarget) this.hudDirTarget.textContent = cardinal(heading)
    }
  }

  #updateElevation(lat, lon) {
    if (!this.hasElevationUrlValue || !this.hasHudElevationTarget) return
    const now = Date.now()
    if (!shouldRefreshElevation(this._lastElevAt, now)) return
    this._lastElevAt = now
    const requestId = (this._elevRequestId || 0) + 1
    this._elevRequestId = requestId
    fetchJson(`${this.elevationUrlValue}?lat=${lat}&lon=${lon}`, { headers: { Accept: "application/json" } })
      .then(d => {
        if (!this.connected || requestId !== this._elevRequestId) return
        if (d.ok && this.hasHudElevationTarget) {
          this.hudElevationTarget.textContent = Math.round(metersToFeet(d.elevation_m)).toLocaleString()
        }
      })
      .catch(error => {
        if (!this.connected || requestId !== this._elevRequestId) return
        logEvent("map.elevation_lookup_failed", error)
        if (this.hasHudElevationTarget) this.hudElevationTarget.textContent = "—"
      })
  }

  search() {
    if (!this.connected || !this.map) return
    const plan = mapLayerSearchPlan({
      searching: this.searching,
      controls: this.layerToggleTargets,
      params: bboxParams(this.map),
      bounds: this.map.getBounds(),
      layerLimits: this.hasLayerLimitsValue ? this.layerLimitsValue : {}
    })
    if (plan.kind === "queue") {
      this.searchQueued = true
      this.searchRequestId += 1
      this.searchAbortController?.abort()
      return
    }
    if (plan.kind === "status") { this.setStatus(plan.status); return }

    this.searching = true
    const requestId = ++this.searchRequestId
    const searchController = typeof AbortController === "undefined" ? null : new AbortController()
    this.searchAbortController = searchController
    this.lastSearchCenter = this.map.getCenter()
    this.setStatus(plan.status)

    Promise.allSettled(plan.activeKeys.map(k => this.#loadLayer(k, plan.params, requestId, searchController?.signal)))
      .then(results => {
        if (!this.#searchCurrent(requestId)) return
        this.setStatus(layerSearchStatus(plan.activeKeys, results, plan.zoomLimitedKeys))
      })
      .catch(error => {
        if (!this.#searchCurrent(requestId)) return
        logEvent("map.layer_search_failed", error)
        this.setStatus(error?.message || "Search failed")
      })
      .finally(() => {
        if (!this.connected) return
        if (this.searchAbortController === searchController) this.searchAbortController = null
        this.searching = false
        if (!this.searchQueued) return
        this.searchQueued = false
        this.search()
      })
  }

  toggleAuto(e) { this.#setAuto(e.currentTarget.checked); this.#savePrefs() }

  #applyBasemap(mode) {
    this.basemapMode = mode
    applyBasemap(this.map, mode, { streetNames: this.#setting("streetNames") })
    this.#renderBasemapChoices()
  }

  #renderBasemapChoices() {
    this.basemapChoiceTargets.forEach(choice => {
      const state = basemapChoiceState(this.basemapMode, choice.dataset.basemap)
      choice.classList.toggle("ring-4", state.selected)
      choice.classList.toggle("ring-slate-900", state.selected)
      choice.classList.toggle("ring-1", !state.selected)
      choice.classList.toggle("ring-slate-200", !state.selected)
      const check = choice.querySelector("[data-basemap-check]")
      if (check) check.classList.toggle("hidden", !state.selected)
    })
  }

  #renderSettings() {
    const settings = { ...WEB_DEFAULT_SETTINGS, ...(this.webSettings || {}) }
    this.element.querySelectorAll("[data-setting-key]").forEach(control => {
      const state = webSettingControlState(settings, {
        key: control.dataset.settingKey,
        type: control.type,
        tagName: control.tagName,
        optionValues: [...(control.options || [])].map(option => option.value)
      })
      if (!state.applies) return
      if ("checked" in state) { control.checked = state.checked; return }
      control.value = state.value
    })
  }

  #showMapSheetTab(tab) {
    const { basemaps } = mapSheetTabState(tab)
    if (this.hasBasemapPanelTarget) this.basemapPanelTarget.classList.toggle("hidden", !basemaps)
    if (this.hasLayersPanelTarget) this.layersPanelTarget.classList.toggle("hidden", basemaps)
    if (this.hasBasemapTabTarget) {
      this.basemapTabTarget.setAttribute("aria-selected", String(basemaps))
      this.basemapTabTarget.classList.toggle("bg-slate-900", basemaps)
      this.basemapTabTarget.classList.toggle("bg-white", !basemaps)
      this.basemapTabTarget.classList.toggle("text-white", basemaps)
      this.basemapTabTarget.classList.toggle("text-slate-500", !basemaps)
    }
    if (this.hasLayersTabTarget) {
      this.layersTabTarget.setAttribute("aria-selected", String(!basemaps))
      this.layersTabTarget.classList.toggle("bg-slate-900", !basemaps)
      this.layersTabTarget.classList.toggle("bg-white", basemaps)
      this.layersTabTarget.classList.toggle("text-white", !basemaps)
      this.layersTabTarget.classList.toggle("text-slate-500", basemaps)
    }
    this.#savePrefs()
  }

  // Enable/disable re-searching whenever the map stops moving. Zoom-only changes
  // keep the data we already have unless the enabled overlays are empty.
  #setAuto(on) {
    if (on) {
      if (!this._auto) { this._auto = () => this.#autoSearchAfterMove(); this.map.on("moveend", this._auto) }
    } else if (this._auto) {
      this.map.off("moveend", this._auto); this._auto = null
      this.#resetMoveIntent()
    }
  }

  #autoOn() { return this.hasAutoSearchTarget && this.autoSearchTarget.checked }

  #autoSearchAfterMove() {
    const needsData = this.#activeLayersNeedData()
    const shouldSearch = shouldAutoSearchAfterMove({
      forceNext: this._forceNextAutoSearch,
      panMove: this._panMove,
      zoomMove: this._zoomMove,
      activeLayersNeedData: needsData,
      centerMoved: this.#centerMovedSinceLastSearch()
    })
    this.#resetMoveIntent()
    if (shouldSearch) this.search()
  }

  #resetMoveIntent() {
    this._panMove = false
    this._zoomMove = false
    this._forceNextAutoSearch = false
  }

  #activeLayersNeedData() {
    this.#ensureLayerState()
    const active = this.layerToggleTargets.filter(t => t.checked).map(t => t.dataset.layer)
    return activeLayersNeedData(active, this.layerFeatureCounts)
  }

  #layerEnabled(key) {
    return this.layerToggleTargets.some(t => t.dataset.layer === key && t.checked)
  }

  #searchCurrent(requestId) {
    return responseIsCurrent({ connected: this.connected, requestId, latestRequestId: this.searchRequestId })
  }

  #centerMovedSinceLastSearch() {
    return centerMovedSinceLastSearch(this.lastSearchCenter, this.map.getCenter())
  }

  // --- internals -----------------------------------------------------------

  #onToggle(toggle) {
    this.#ensureLayerState()
    // Unchecking clears the overlay (like the old clearLayers); re-checking
    // shows nothing until the next search fills it.
    if (!toggle.checked) this.#clearLayer(toggle.dataset.layer)
    this.#savePrefs()
  }

  // --- persisted control prefs (local browser + signed-in account) ---------

  #loadPrefs() {
    const local = readStoredMapPrefs()
    const server = this.hasPreferencesValue ? this.preferencesValue : {}
    const p = initialMapPreferences(server, local)
    const migrateLocalToAccount = this.signedInValue && this.hasPreferencesUrlValue &&
      Object.keys(server).length === 0 && Object.keys(local).length > 0
    const state = mapPrefsLoadState(p)
    if (!Number(p.settings?.followPitch3d) && Number(state.settings.followPitch) > 0) {
      state.settings.followPitch3d = state.settings.followPitch
    }
    this.webSettings = state.settings
    this.layerToggleTargets.forEach(t => {
      if (t.dataset.layer in state.layers) t.checked = state.layers[t.dataset.layer]
    })
    this.useFilterTargets.forEach(t => {
      if (t.dataset.use in state.uses) t.checked = state.uses[t.dataset.use]
    })
    if (state.auto != null && this.hasAutoSearchTarget) this.autoSearchTarget.checked = state.auto
    if (state.basemap) this.basemapMode = state.basemap
    writeStoredMapPrefs(p)
    this.#renderSettings()
    if (migrateLocalToAccount) this.#savePrefs()
  }

  #savePrefs() {
    const preferences = buildStoredMapPrefs(readStoredMapPrefs(), {
      layerControls: this.layerToggleTargets,
      useControls: this.useFilterTargets,
      autoChecked: this.hasAutoSearchTarget ? this.autoSearchTarget.checked : null,
      basemapMode: this.basemapMode,
      settings: this.webSettings
    })
    writeStoredMapPrefs(preferences)
    this.#queuePreferencesSave(preferences)
  }

  #queuePreferencesSave(preferences) {
    if (!this.signedInValue || !this.hasPreferencesUrlValue) return
    this._pendingPreferences = preferences
    if (this._preferencesSaving) return
    void this.#flushPreferences()
  }

  async #flushPreferences() {
    this._preferencesSaving = true
    try {
      while (this._pendingPreferences) {
        const preferences = this._pendingPreferences
        this._pendingPreferences = null
        await saveMapPreferences(this.preferencesUrlValue, preferences)
      }
    } catch (error) {
      logEvent("map.preferences_save_failed", error)
      if (this.connected) this.setStatus("Map settings saved on this device; account sync failed.")
    } finally {
      this._preferencesSaving = false
    }
  }

  // --- overlay sources + layers (once, at boot) -----------------------------

  #ensureLayerState() {
    this.layers ||= {}
    this.managedTrailFeatures ||= {}
    this._featureData ||= {}
    this.layerFeatureCounts ||= {}
  }

  #addOverlayLayers() {
    this.#ensureLayerState()
    const empty = { type: "FeatureCollection", features: [] }
    const sourceIds = [
      ...GEOJSON_SOURCE_IDS,
      WEB_TRACK_LINES_POLICY.friendTrackSourceId,
      WEB_TRACK_LINES_POLICY.selfTrackSourceId
    ]
    sourceIds.forEach(id => {
      this.map.addSource(id, { type: "geojson", data: empty })
    })
    overlayLayerDefinitions().forEach(layer => this.map.addLayer(layer))

    // Breadcrumb trails (mine while recording, friends' from presence): two
    // dedicated GeoJSON line layers, styled like the other lines. Their sources
    // are registered above before any layer references them.
    trackLineLayerDefinitions().forEach(layer => this.map.addLayer(layer))

    // One click handler for every vector overlay: topmost rendered feature wins,
    // resolved back to the ORIGINAL feature (by the id we assign on load) so
    // popups see real nested properties.
    this.map.on("click", e => {
      if (mapOverlayOwnsClick(e.originalEvent?.target)) return
      const layers = INTERACTIVE_LAYERS.filter(l => this.map.getLayer(l))
      const hit = this.map.queryRenderedFeatures(e.point, { layers })[0]
      if (!hit) return
      const key = vectorLayerKeyForStyleLayer(hit.layer.id)
      const feature = this._featureData[key]?.[hit.id]
      if (feature) this.#openFeaturePopup(key, feature, e.lngLat)
    })
    INTERACTIVE_LAYERS.forEach(l => {
      this.map.on("mouseenter", l, () => { this.map.getCanvas().style.cursor = "pointer" })
      this.map.on("mouseleave", l, () => { this.map.getCanvas().style.cursor = "" })
    })
  }

  #openFeaturePopup(key, feature, lngLat) {
    const plan = featurePopupPlan(key, feature.properties || {})
    if (!plan) return
    if (plan.selectTrail) this.#selectTrail(feature)
    new maplibregl.Popup({ maxWidth: MAP_POPUP_MAX_WIDTH }).setLngLat(lngLat).setHTML(plan.html).addTo(this.map)
  }

  #setSourceData(id, features) {
    if (!this.connected || !this.map) return
    this.map.getSource(id)?.setData({ type: "FeatureCollection", features })
  }

  #clearLayer(key) {
    this.#ensureLayerState()
    if (!this.layers[key]) this.layers[key] = { url: null, markers: [] }
    const plan = clearLayerPlan(key)
    this.layers[key].markers.forEach(m => m.remove())
    this.layers[key].markers = []
    this._featureData[key] = []
    if (plan.clearTrailSelection) this.#clearTrailSelection()
    if (plan.clearManagedTrailFeatures) this.managedTrailFeatures[key] = []
    plan.sourceIds.forEach(sourceId => this.#setSourceData(sourceId, []))
  }

  async #loadLayer(key, params, requestId, searchSignal = null) {
    this.#ensureLayerState()
    const layer = this.layers[key]
    if (!layer?.url) return `${key}: not ready`
    const data = await this.#fetchLayerJson(`${layer.url}?${params}`, searchSignal)
    if (!this.#searchCurrent(requestId)) return `${key}: stale`
    if (!this.#layerEnabled(key)) return `${key}: off`
    const plan = layerLoadPlan(key, data)
    this.layerFeatureCounts[key] = plan.featureCount
    if (!plan.ok) return plan.message

    this.#clearLayer(key)

    if (plan.kind === "managedTrail") {
      this.managedTrailFeatures[key] = plan.features
      this._featureData[key] = plan.features
      this.#renderManagedTrailLayer(key)
      return plan.message
    }

    if (plan.kind === "geojsonSource") {
      this._featureData[plan.featureDataKey] = plan.features
      this.#setSourceData(plan.sourceId, plan.features)
      if (plan.labelFeatures) plan.features.forEach(f => this.#addParcelLabel(f))
      return plan.message
    }

    for (const m of plan.markers) this.#addPinMarker(key, m)
    return plan.message
  }

  async #fetchLayerJson(url, searchSignal = null) {
    if (typeof AbortController === "undefined") {
      return fetchJson(url, { headers: { Accept: "application/json" } })
    }

    const controller = new AbortController()
    const abortSuperseded = () => controller.abort()
    searchSignal?.addEventListener("abort", abortSuperseded, { once: true })
    const timeoutId = setTimeout(() => controller.abort(), LAYER_SEARCH_TIMEOUT_MS)
    try {
      return await fetchJson(url, { headers: { Accept: "application/json" }, signal: controller.signal })
    } catch (error) {
      if (error?.name === "AbortError") {
        throw new Error(searchSignal?.aborted ? "request superseded" : "request timed out")
      }
      throw error
    } finally {
      clearTimeout(timeoutId)
      searchSignal?.removeEventListener("abort", abortSuperseded)
    }
  }

  // A DOM pin marker (usgs / fas / recgov). The wrapper div is the marker
  // element (MapLibre owns its transform); the .pin inside keeps its CSS rotate.
  #addPinMarker(key, m) {
    const plan = pinMarkerPlan(key, m)
    const el = document.createElement("div")
    el.innerHTML = plan.elementHtml
    el.style.cursor = plan.cursor
    const marker = new maplibregl.Marker({ element: el, anchor: plan.anchor }).setLngLat(plan.lngLat)
    // USGS gauges open the readings modal; everything else uses a popup.
    if (plan.siteEvent) {
      el.addEventListener("click", e => {
        e.stopPropagation()
        window.dispatchEvent(new CustomEvent(WEB_EVENTS.usgsSite, { detail: plan.siteEvent }))
      })
    } else {
      marker.setPopup(new maplibregl.Popup(plan.popupOptions).setHTML(plan.popupHtml))
    }
    marker.addTo(this.map)
    this.layers[key].markers.push(marker)
  }

  #addParcelLabel(feature) {
    const owner = feature.properties?.owner
    if (!owner) return
    const at = polygonLabelPoint(feature.geometry)
    if (!at) return
    const el = document.createElement("div")
    el.className = "parcel-label"
    el.textContent = owner
    const marker = new maplibregl.Marker({ element: el }).setLngLat(at).addTo(this.map)
    this.layers.parcels.markers.push(marker)
  }

  #selectTrail(feature) {
    this.#setSourceData("src-trails-selected", [feature])

    if (this.trailStartMarker) { this.trailStartMarker.remove(); this.trailStartMarker = null }
    const plan = trailStartMarkerPlan(feature)
    if (!plan) return

    const el = document.createElement("div")
    el.innerHTML = plan.elementHtml
    this.trailStartMarker = new maplibregl.Marker({ element: el, anchor: plan.anchor })
      .setLngLat(plan.lngLat)
      .setPopup(new maplibregl.Popup(plan.popupOptions).setHTML(plan.popupHtml))
      .addTo(this.map)
  }

  #clearTrailSelection() {
    if (this.trailStartMarker) this.trailStartMarker.remove()
    this.trailStartMarker = null
    this.#setSourceData("src-trails-selected", [])
  }

  // --- Trails/routes (lines, filtered by allowed use) ----------------------

  filterTrailUses() {
    MANAGED_TRAIL_LAYERS.forEach(key => this.#renderManagedTrailLayer(key))
    this.#savePrefs()
  }

  filterUsfs() { this.filterTrailUses() }

  #renderManagedTrailLayer(key) {
    const source = MANAGED_TRAIL_SOURCES[key]
    if (!source) return
    const selectedUses = this.#selectedUses()
    const feats = (this.managedTrailFeatures[key] || []).filter(f => trailUseAllowed(f.properties, selectedUses))
    this.#setSourceData(source, feats)
  }

  #selectedUses() {
    return selectedTrailUseKeys(this.useFilterTargets)
  }

  #lngLat([lat, lng]) { return [lng, lat] }

  // --- breadcrumb trail lines (self-track + friend tracks) -------------------

  #renderSelfTrack() {
    const feature = selfTrackFeature(this._selfTrack || [])
    this.#setSourceData(WEB_TRACK_LINES_POLICY.selfTrackSourceId, feature ? [feature] : [])
  }

  #renderFriendTracks(tracks = {}) {
    this.#setSourceData(WEB_TRACK_LINES_POLICY.friendTrackSourceId, friendTrackFeatures(tracks))
  }

  setStatus(s) {
    if (this.connected && this.hasStatusTarget) this.statusTarget.textContent = s
  }
}
